import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { apiClient } from '../lib/apiClient';
import LanguageToggle from '../components/LanguageToggle';
import {
  ShoppingCart, Plus, Lock, CheckCircle2, Loader2, ArrowLeft, LogOut,
} from 'lucide-react';

/**
 * Phase 75 — Stand Services browser, integrated with the unified shopping cart.
 *
 * Design (revised 2026-04-28 per Murat Bey):
 *   • Drop the page-local cart and "My Orders" tabs.
 *   • Each "Add" click POSTs to the same /api/customer/cart/add endpoint
 *     the rest of the catalog (supplier/organisation products) uses, with
 *     `requires_stand_allocation: true` so the backend enforces the
 *     allocation guard. The user reviews + checks out from the existing
 *     dashboard cart icon — single, unified flow.
 *   • Allocation lock view stays (returns early if no hall_stand).
 */
export default function CustomerStandServicesPage() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const { language } = useLanguage();
  const tr = language === 'tr';
  const t = (en, trTxt) => (tr ? trTxt : en);

  const [catalog, setCatalog] = useState(null);   // { allocated, stand, categories }
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState({});       // { product_id: bool }
  const [addedIds, setAddedIds] = useState({});   // { product_id: count }
  const [toast, setToast] = useState(null);

  const fmtMoney = (v, cur) => new Intl.NumberFormat(tr ? 'tr-TR' : 'en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(Number(v) || 0) + ' ' + (cur || '');

  useEffect(() => {
    (async () => {
      try { const res = await apiClient.get('/stand-services/catalog'); setCatalog(res.data); }
      catch (e) { console.error(e); }
      setLoading(false);
    })();
    // Fire-and-forget: clear unseen badge as soon as user opens the page.
    apiClient.post('/stand-services/mark-seen').catch(() => {});
  }, []);

  const addToCart = async (product, category) => {
    setAdding(s => ({ ...s, [product.id]: true }));
    try {
      const payload = {
        product_id:                product.id,
        product_name:              tr && product.title_tr ? product.title_tr : (product.title_en || product.title),
        unit_price:                Number(product.price) || 0,
        currency:                  product.currency || 'USD',
        quantity:                  product.min_quantity || 1,
        supplier_tenant_id:        'stand_services',
        calculation_type:          'flat',
        notes:                     `[Stand Service] ${tr && category?.name_tr ? category.name_tr : category?.name || ''}`,
        min_qty:                   product.min_quantity || 1,
        max_qty:                   product.max_quantity || 999,
        requires_stand_allocation: !!product.stand_allocation_required,
      };
      await apiClient.post('/customer/cart/add', payload);
      // Notify the rest of the app (CustomerDashboard cart drawer/badge,
      // checkout page, allocation gate) that the cart changed — silent
      // refresh, no flicker.
      try { window.dispatchEvent(new CustomEvent('cop31:cart-changed')); } catch { /* no-op */ }
      setAddedIds(s => ({ ...s, [product.id]: (s[product.id] || 0) + 1 }));
      setToast({ type: 'success', text: t(`Added "${payload.product_name}" to cart`, `"${payload.product_name}" sepete eklendi`) });
      setTimeout(() => setToast(null), 3000);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail.message : (detail || e.message);
      setToast({ type: 'error', text: msg });
      setTimeout(() => setToast(null), 4500);
    }
    setAdding(s => ({ ...s, [product.id]: false }));
  };

  // ── Not allocated view ──
  if (!loading && catalog && !catalog.allocated) {
    return (
      <Shell slug={slug} navigate={navigate}>
        <div className="max-w-2xl mx-auto mt-10">
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 text-center">
            <Lock className="w-10 h-10 text-amber-600 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-amber-900 mb-2">{t('Stand Services Locked','Servisler Kilitli')}</h2>
            <p className="text-sm text-amber-800 mb-4">
              {t('You must have an allocated stand to browse and order stand services. Please complete your stand request and wait for admin allocation.',
                 'Servis kataloğunu görüntüleyip sipariş verebilmek için atanmış bir standınız olması gerekir. Lütfen önce stand talebinizi tamamlayın.')}
            </p>
            <button onClick={() => navigate(`/musteri-admin/${slug}/stand-design`)}
              className="px-5 py-2.5 rounded-xl text-white text-sm font-semibold shadow"
              style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}>
              {t('Go to Stand Request','Stand Talebine Git')}
            </button>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell slug={slug} navigate={navigate}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">{t('Stand Services','Stand Servisleri')}</h1>
            {catalog?.stand && (
              <p className="text-sm text-gray-500 mt-1">
                {t('Ordering for','Sipariş veren')}: <strong>{catalog.stand.stand_no}</strong> · {catalog.stand.hall_name}
              </p>
            )}
          </div>
          <button
            onClick={() => navigate(`/musteri-admin/${slug}/dashboard`)}
            data-testid="goto-cart-btn"
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-semibold shadow-md"
            style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}>
            <ShoppingCart className="w-4 h-4" /> {t('Go to Cart','Sepete Git')}
          </button>
        </div>

        {/* Toast */}
        {toast && (
          <div className={`fixed top-24 right-6 z-50 max-w-sm px-4 py-3 rounded-xl shadow-lg text-sm font-semibold flex items-center gap-2 ${
            toast.type === 'success' ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'
          }`}>
            {toast.type === 'success' && <CheckCircle2 className="w-5 h-5 flex-shrink-0" />}
            <span>{toast.text}</span>
          </div>
        )}

        {loading && <div className="flex items-center justify-center py-12 text-gray-400"><Loader2 className="w-5 h-5 animate-spin mr-2" /> {t('Loading','Yükleniyor...')}</div>}

        {!loading && catalog?.categories?.length === 0 && (
          <div className="text-center text-gray-400 py-16">{t('No services available yet.','Henüz hiçbir servis tanımlanmadı.')}</div>
        )}

        {!loading && catalog?.categories?.map(cat => (
          <div key={cat.id} className="mb-6">
            <h2 className="font-bold text-gray-800 text-lg mb-3">{tr && cat.name_tr ? cat.name_tr : (cat.name_en || cat.name)}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {cat.products.map(p => {
                const addCount = addedIds[p.id] || 0;
                const isAdding = !!adding[p.id];
                return (
                  <div key={p.id} data-testid={`product-${p.id}`} className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col">
                    <h3 className="font-semibold text-gray-800 mb-1">{tr && p.title_tr ? p.title_tr : (p.title_en || p.title)}</h3>
                    {p.description && <p className="text-xs text-gray-500 mb-2 line-clamp-2">{tr && p.description_tr ? p.description_tr : (p.description_en || p.description)}</p>}
                    {p.approval_required && <p className="text-xs text-amber-600 mb-1">⚠ {t('Requires approval','Onay gerekir')}</p>}
                    <div className="mt-auto pt-3 flex items-center justify-between border-t border-gray-100">
                      <div className="text-lg font-bold text-gray-800 tabular-nums">{fmtMoney(p.price, '')} <span className="text-xs text-gray-500">{p.currency}/{p.unit_type}</span></div>
                      <button
                        onClick={() => addToCart(p, cat)}
                        disabled={isAdding}
                        data-testid={`add-${p.id}`}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 disabled:opacity-50 ${
                          addCount > 0 ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' : 'bg-purple-600 text-white hover:bg-purple-700'
                        }`}>
                        {isAdding ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                        {addCount > 0 ? `×${addCount} ${t('Added','Eklendi')}` : t('Add','Ekle')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </Shell>
  );
}

function Shell({ children, slug, navigate }) {
  const handleLogout = () => { localStorage.clear(); navigate('/giris'); };
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <nav className="sticky top-0 z-40 border-b border-white/10" style={{ background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8add 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate(`/musteri-admin/${slug}/dashboard`)} className="flex items-center gap-2 text-white hover:bg-white/10 rounded-lg px-2 py-1 transition-colors">
                <ArrowLeft className="w-4 h-4" />
                <span className="text-xs font-semibold">Dashboard</span>
              </button>
              <div className="flex items-center gap-2 ml-2">
                <div className="h-9 w-9 rounded-xl bg-white/20 flex flex-col items-center justify-center">
                  <span className="text-white font-black text-[9px] leading-none tracking-tight">COP</span>
                  <span className="text-white font-black text-sm leading-none">31</span>
                </div>
                <div>
                  <div className="text-white font-bold text-sm">COP 31 Antalya</div>
                  <p className="text-white/60 text-[10px]">9-20 Kasım 2026</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <LanguageToggle variant="admin" />
              <button onClick={handleLogout} className="flex items-center gap-2 text-white hover:bg-white/10 rounded-full px-3 py-1.5 text-xs font-semibold">
                <LogOut className="w-4 h-4" /> Çıkış
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="flex-1 p-6">{children}</main>

      <footer className="bg-white border-t border-gray-200 py-4 text-center text-xs text-gray-500">
        © 2026 COP 31 Antalya · Stand Services Module
      </footer>
    </div>
  );
}
